import { createAsyncThunk } from '@reduxjs/toolkit';
import moment from 'moment';
import { billingProcessService } from 'services';
import { t_billing_process } from 'types';

export const getOptionYear = createAsyncThunk('payout/getOptionYear', async () => {
  try {
    const { data } = await billingProcessService.searchBills({
      page: 1,
      condition: [],
      sort_fields: [
        {
          id: 'invoice_month',
          order: 'asc',
        },
      ],
      sort_field_id: 'created_at',
      sort_order: 'asc',
      include_lookups: false,
    });
    if (data.totalItems > 0) {
      const currentYear = new Date().getFullYear() - (new Date().getMonth() === 0 ? 1 : 0);
      const yearFar = data.items[0].invoice_month ? data.items[0].invoice_month.slice(0, 4) : currentYear;
      const response: string[] = [yearFar];
      for (let index = 0; index < currentYear - Number(yearFar); index++) {
        response.push((Number(yearFar) + index + 1).toString());
      }
      return response;
    }
  } catch (error) {
    return [];
  }
});

export const searchBill = createAsyncThunk<any, any>('payout/searchBill', async (param) => {
  try {
    const { data } = await billingProcessService.searchBills({
      page: 1,
      per_page: 0,
      conditions: [
        {
          id: 'invoice_month',
          search_value: [param.invoice_month],
        },
        {
          id: 'payment_method',
          search_value: [param.payment_method],
        },
        {
          id: 'Status',
          search_value: ['billing', 'issued', 'payment', 'closed'],
        },
      ],
      sort_field_id: 'facility_code',
      sort_order: 'asc',
      include_lookups: false,
    });
    if (data.totalItems < 1) {
      return {
        bills: data,
        paymentAdded: 0,
        paymentNoAdd: 0,
      };
    }
    const { data: paymentNo } = await billingProcessService.searchBills({
      page: 1,
      per_page: 0,
      conditions: [
        {
          id: 'invoice_month',
          search_value: [param.invoice_month],
        },
        {
          id: 'payment_method',
          search_value: [param.payment_method],
        },
        {
          id: 'Status',
          search_value: ['billing', 'issued'],
        },
      ],
      sort_field_id: 'facility_code',
      sort_order: 'asc',
      include_lookups: false,
    });

    const paymentAdded =
      data.totalItems > 0
        ? data.items
            .map((value: any) => {
              if ('payment_amount' in value && value.payment_amount) {
                return Number(value.payment_amount);
              }
              return 0;
            })
            .reduce((acc: number, cur: number) => acc + cur)
        : 0;

    return {
      bills: {
        items: data.items.slice(0, 20),
        totalItems: data.totalItems,
      },
      paymentAdded: paymentAdded,
      paymentNoAdd: paymentNo.totalItems,
    };
  } catch (error) {
    return error;
  }
});

export const filterBill = createAsyncThunk<any, any>('payout/filterBill', async (param) => {
  try {
    const conditions = [];
    param.facility_code &&
      conditions.push({
        id: 'facility_code',
        search_value: [param.facility_code],
      });
    param.facility_name &&
      conditions.push({
        id: 'facility_name',
        search_value: [param.facility_name],
      });
    param.account_name &&
      conditions.push({
        id: 'account_name',
        search_value: [param.account_name],
      });
    const { data } = await billingProcessService.searchBills({
      page: param.page,
      conditions: [
        {
          id: 'invoice_month',
          search_value: [param.invoice_month],
        },
        {
          id: 'payment_method',
          search_value: [param.payment_method],
        },
        {
          id: 'Status',
          search_value: ['billing', 'issued', 'payment', 'closed'],
        },
        ...conditions,
      ],
      sort_field_id: 'facility_code',
      sort_order: 'asc',
      include_lookups: false,
    });
    return data;
  } catch (error) {
    return error;
  }
});

export const getDataExport = createAsyncThunk<any, { year: string; month: string }>(
  'payout/getDataExport',
  async (param) => {
    try {
      const formatter = new Intl.NumberFormat('ja-JP', {
        style: 'currency',
        currency: 'JPY',
      });

      const { data: tab1 } = await billingProcessService.searchBills({
        page: 1,
        per_page: 0,
        conditions: [
          {
            id: 'invoice_month',
            search_value: [`${param.year}${param.month}`],
          },
          {
            id: 'payment_method',
            search_value: ['カード'],
          },
        ],
        sort_field_id: 'facility_code',
        sort_order: 'asc',
        include_lookups: false,
      });
      const { data: tab2 } = await billingProcessService.searchBills({
        page: 1,
        per_page: 0,
        conditions: [
          {
            id: 'invoice_month',
            search_value: [`${param.year}${param.month}`],
          },
          {
            id: 'payment_method',
            search_value: ['振込'],
          },
        ],
        sort_field_id: 'facility_code',
        sort_order: 'asc',
      });
      const { data: tab3 } = await billingProcessService.searchBills({
        page: 1,
        per_page: 0,
        conditions: [
          {
            id: 'invoice_month',
            search_value: [`${param.year}${param.month}`],
          },
          {
            id: 'payment_method',
            search_value: ['振替'],
          },
        ],
        sort_field_id: 'facility_code',
        sort_order: 'asc',
      });

      const titleTab1 = `カード＜(株)スリープパートナーズ　${param.year}年${Number(
        param.month,
      )}月分　請求金額・入金金額リスト　（${Number(param.month) + 2 > 12 ? Number(param.year) + 1 : param.year}年${
        Number(param.month) + 2 > 12 ? Number(param.month) - 10 : Number(param.month) + 2
      }月入金）＞`; //カードは２ヶ月後
      const titleTab2 = `振込＜(株)スリープパートナーズ　${param.year}年${Number(
        param.month,
      )}月分　請求金額・入金金額リスト　（${Number(param.month) + 1 > 12 ? Number(param.year) + 1 : param.year}年${
        Number(param.month) + 1 > 12 ? 1 : Number(param.month) + 1
      }月振込）＞`;
      const titleTab3 = `振替＜(株)スリープパートナーズ　${param.year}年${Number(
        param.month,
      )}月分　請求金額・入金金額リスト　（${
        tab3.totalItems > 0 && tab3.items[0].payment_due_date
          ? moment(tab3.items[0].payment_due_date).format('YYYY年M月DD日')
          : `${Number(param.month) + 1 > 12 ? Number(param.year) + 1 : param.year}年${
              Number(param.month) + 1 > 12 ? 1 : Number(param.month) + 1
            }月27日`
      }振替）＞`;
      const total = {
        tab1: {
          total_billing: 0,
          total_payment: 0,
          total_carried_forward: 0,
        },
        tab2: {
          total_billing: 0,
          total_payment: 0,
          total_carried_forward: 0,
        },
        tab3: {
          total_billing: 0,
          total_payment: 0,
          total_carried_forward: 0,
        },
      };

      const itemTab1 =
        tab1.totalItems < 1
          ? []
          : tab1.items.map((value: t_billing_process) => {
              if (value.billing_amount) {
                total.tab1.total_billing += Number(value.billing_amount);
              }
              if (value.payment_amount) {
                total.tab1.total_payment += Number(value.payment_amount);
              }
              if (value.carried_forward) {
                total.tab1.total_carried_forward += Number(value.carried_forward);
              }
              let cpap_price =
                Number(value.cpap_count ? value.cpap_count : 0) *
                Number(value.cpap_unit_price ? value.cpap_unit_price : 0);
              let cpap_sp_price =
                Number(value.cpap_sp_count ? value.cpap_sp_count : 0) *
                Number(value.cpap_unit_sp_price ? value.cpap_unit_sp_price : 0);
              let psg_price =
                Number(value.psg_count ? value.psg_count : 0) * (value.psg_unit_price ? value.psg_unit_price : 0);
              let simplicity_price =
                Number(value.simplicity_count ? value.simplicity_count : 0) *
                Number(value.simplicity_unit_price ? value.simplicity_unit_price : 0);
              return {
                facility_code: value.facility_code ?? '',
                facility_id: value.facility_id ?? '',
                account_name: value.account_name ?? '',
                cpap_unit_price: value.cpap_unit_price ? value.cpap_unit_price : 0,
                cpap_unit_sp_price: value.cpap_unit_sp_price ? value.cpap_unit_sp_price : 0,
                psg_unit_price: value.psg_unit_price ? value.psg_unit_price : 0,
                simplicity_unit_price: value.simplicity_unit_price ? value.simplicity_unit_price : 0,
                billing_amount: value.billing_amount ? value.billing_amount : 0,
                payment_amount: value.payment_amount ? value.payment_amount : 0,
                payment_remark: value.payment_remark ?? '',
                carryover_amount: value.carryover_amount ? value.carryover_amount : 0,
                carried_forward: value.carried_forward ? value.carried_forward : 0,
                cpap_count: value.cpap_count ? value.cpap_count : 0,
                cpap_sp_count: value.cpap_sp_count ? value.cpap_sp_count : 0,
                psg_count: value.psg_count ? value.psg_count : 0,
                simplicity_count: value.simplicity_count ? value.simplicity_count : 0,
                subtotal: value.subtotal ? value.subtotal : 0,
                cpap_price: cpap_price,
                cpap_sp_price: cpap_sp_price,
                simplicity_price: simplicity_price,
                psg_price: psg_price,
                other_price:
                  Number(value.subtotal ? value.subtotal : 0) -
                  cpap_price -
                  cpap_sp_price -
                  psg_price -
                  simplicity_price,
              };
            });
      const itemTab2 =
        tab2.totalItems < 1
          ? []
          : tab2.items.map((value: t_billing_process) => {
              if (value.billing_amount) {
                total.tab2.total_billing += Number(value.billing_amount);
              }
              if (value.payment_amount) {
                total.tab2.total_payment += Number(value.payment_amount);
              }
              if (value.carried_forward) {
                total.tab2.total_carried_forward += Number(value.carried_forward);
              }
              let cpap_price =
                Number(value.cpap_count ? value.cpap_count : 0) *
                Number(value.cpap_unit_price ? value.cpap_unit_price : 0);
              let cpap_sp_price =
                Number(value.cpap_sp_count ? value.cpap_sp_count : 0) *
                Number(value.cpap_unit_sp_price ? value.cpap_unit_sp_price : 0);
              let psg_price =
                Number(value.psg_count ? value.psg_count : 0) * (value.psg_unit_price ? value.psg_unit_price : 0);
              let simplicity_price =
                Number(value.simplicity_count ? value.simplicity_count : 0) *
                Number(value.simplicity_unit_price ? value.simplicity_unit_price : 0);
              return {
                facility_code: value.facility_code ?? '',
                facility_id: value.facility_id ?? '',
                account_name: value.account_name ?? '',
                cpap_unit_price: value.cpap_unit_price ? value.cpap_unit_price : 0,
                cpap_unit_sp_price: value.cpap_unit_sp_price ? value.cpap_unit_sp_price : 0,
                psg_unit_price: value.psg_unit_price ? value.psg_unit_price : 0,
                simplicity_unit_price: value.simplicity_unit_price ? value.simplicity_unit_price : 0,
                billing_amount: value.billing_amount ? value.billing_amount : 0,
                payment_amount: value.payment_amount ? value.payment_amount : 0,
                payment_remark: value.payment_remark ?? '',
                payment_fees: value.payment_fees ? value.payment_fees : '',
                facility: value.lookup_items.facility_id,
                payment_date: value.payment_date ? moment(value.payment_date).format('M/D') : '',
                payment_fees_amount: value.payment_fees ? (value.payment_amount ?? 0) - value.payment_fees : '',
                carryover_amount: value.carryover_amount ? value.carryover_amount : 0,
                carried_forward: value.carried_forward ? value.carried_forward : 0,
                cpap_count: value.cpap_count ? value.cpap_count : 0,
                cpap_sp_count: value.cpap_sp_count ? value.cpap_sp_count : 0,
                psg_count: value.psg_count ? value.psg_count : 0,
                simplicity_count: value.simplicity_count ? value.simplicity_count : 0,
                subtotal: value.subtotal ? value.subtotal : 0,
                cpap_price: cpap_price,
                cpap_sp_price: cpap_sp_price,
                simplicity_price: simplicity_price,
                psg_price: psg_price,
                other_price:
                  Number(value.subtotal ? value.subtotal : 0) -
                  cpap_price -
                  cpap_sp_price -
                  psg_price -
                  simplicity_price,
              };
            });
      const itemTab3 =
        tab3.totalItems < 1
          ? []
          : tab3.items.map((value: t_billing_process) => {
              if (value.billing_amount) {
                total.tab3.total_billing += Number(value.billing_amount);
              }
              if (value.payment_amount) {
                total.tab3.total_payment += Number(value.payment_amount);
              }
              if (value.carried_forward) {
                total.tab3.total_carried_forward += Number(value.carried_forward);
              }
              let cpap_price =
                Number(value.cpap_count ? value.cpap_count : 0) *
                Number(value.cpap_unit_price ? value.cpap_unit_price : 0);
              let cpap_sp_price =
                Number(value.cpap_sp_count ? value.cpap_sp_count : 0) *
                Number(value.cpap_unit_sp_price ? value.cpap_unit_sp_price : 0);
              let psg_price =
                Number(value.psg_count ? value.psg_count : 0) * (value.psg_unit_price ? value.psg_unit_price : 0);
              let simplicity_price =
                Number(value.simplicity_count ? value.simplicity_count : 0) *
                Number(value.simplicity_unit_price ? value.simplicity_unit_price : 0);
              return {
                facility_code: value.facility_code ?? '',
                facility_id: value.facility_id ?? '',
                account_name: value.account_name ?? '',
                cpap_unit_price: value.cpap_unit_price ? value.cpap_unit_price : 0,
                cpap_unit_sp_price: value.cpap_unit_sp_price ? value.cpap_unit_sp_price : 0,
                psg_unit_price: value.psg_unit_price ? value.psg_unit_price : 0,
                simplicity_unit_price: value.simplicity_unit_price ? value.simplicity_unit_price : 0,
                billing_amount: value.billing_amount ? value.billing_amount : 0,
                payment_amount: value.payment_amount ? value.payment_amount : 0,
                payment_remark: value.payment_remark ?? '',
                carryover_amount: value.carryover_amount ? value.carryover_amount : 0,
                carried_forward: value.carried_forward ? value.carried_forward : 0,
                cpap_count: value.cpap_count ? value.cpap_count : 0,
                cpap_sp_count: value.cpap_sp_count ? value.cpap_sp_count : 0,
                psg_count: value.psg_count ? value.psg_count : 0,
                simplicity_count: value.simplicity_count ? value.simplicity_count : 0,
                subtotal: value.subtotal ? value.subtotal : 0,
                cpap_price: cpap_price,
                cpap_sp_price: cpap_sp_price,
                simplicity_price: simplicity_price,
                psg_price: psg_price,
                other_price:
                  Number(value.subtotal ? value.subtotal : 0) -
                  cpap_price -
                  cpap_sp_price -
                  psg_price -
                  simplicity_price,
              };
            });

      return {
        tab1: {
          title: titleTab1,
          items: itemTab1,
          total_billing: total.tab1.total_billing,
          total_payment: total.tab1.total_payment,
          total_carried_forward: total.tab1.total_carried_forward,
        },
        tab2: {
          title: titleTab2,
          items: itemTab2,
          total_billing: total.tab2.total_billing,
          total_payment: total.tab2.total_payment,
          total_carried_forward: total.tab2.total_carried_forward,
        },
        tab3: {
          title: titleTab3,
          items: itemTab3,
          total_billing: total.tab3.total_billing,
          total_payment: total.tab3.total_payment,
          total_carried_forward: total.tab3.total_carried_forward,
        },
      };
    } catch (error) {
      return error;
    }
  },
);

export const updateInvoice = createAsyncThunk<any, t_billing_process>('payout/updateInvoice', async (param) => {
  try {
    const paymentDate = moment(param.payment_date);

    if (param.Status === '請求書登録済') {
      return billingProcessService.executeAction({
        i_id: param.i_id,
        action: 'a_payment',
        item: { ...param, payment_date: paymentDate },
      });
    }
    if (param.Status === '請求書発行済') {
      return billingProcessService.executeAction({
        i_id: param.i_id,
        action: 'a_issued_to_payment',
        item: { ...param, payment_date: paymentDate },
      });
    }
    return billingProcessService.updateInvoice({ ...param, payment_date: paymentDate });
  } catch (error) {
    return error;
  }
});
